/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: GoogleVoiceConfiguration.tsx
 🗓️ Created: 2025-01-19
 🧩 Description:
    Voice configuration component for Google Cloud TTS with
    cascading selectors: Language → Accent → Voice → Style
    Audio controls shown after complete selection.
    Sub-components: GoogleVoiceAudioControls, GoogleVoiceTestPanel
    Parser utils: googleVoiceParsers.ts
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Volume2, Globe, MapPin, Mic, Music } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import Select from '../../../../components/ui/select';
import Combobox from '../../../../components/ui/Combobox';
import { FormBox } from '../../../../components/ui/FormBox';
import { useI18n } from '../../../../hooks/useI18n';
import { AiProfile } from '../../types';
import { api } from '../../../../lib/api';
import {
    extractUniqueLanguages,
    extractAccentsForLanguage,
    extractVoicesForAccent,
} from '../../utils/googleVoiceParsers';
import { GoogleVoiceAudioControls } from './GoogleVoiceAudioControls';
import { GoogleVoiceTestPanel } from './GoogleVoiceTestPanel';

interface GoogleVoiceConfigurationProps {
    formData: AiProfile;
    availableVoices: string[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onInputChange: (field: string, value: any) => void;
    onTestVoice?: () => void;
}

const VOICE_STYLES = [
    { value: 'neutral', label: 'Natural' },
    { value: 'cheerful', label: 'Alegre' },
    { value: 'sad', label: 'Triste' },
    { value: 'whisper', label: 'Susurrar' },
    { value: 'narration', label: 'Storyteller' },
];

interface GoogleVoiceConfig {
    language: string;
    accent: string;
    voice: string;
    voiceFullName: string;
    voiceApiName: string;
    gender: string;
    style: string;
    speakingRate: number;
    pitch: number;
    volumeGainDb: number;
}

function parseInitialConfig(rawConfig: unknown): GoogleVoiceConfig {
    const defaults: GoogleVoiceConfig = {
        language: '', accent: '', voice: '', voiceFullName: '', voiceApiName: '',
        gender: '', style: 'neutral', speakingRate: 1.0, pitch: 0, volumeGainDb: 0,
    };
    try {
        if (rawConfig) {
            const parsed = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig as Partial<GoogleVoiceConfig>;
            return { ...defaults, ...parsed };
        }
    } catch { /* use defaults */ }
    return defaults;
}

export default function GoogleVoiceConfiguration({
    formData, availableVoices, onInputChange,
}: GoogleVoiceConfigurationProps) {
    const { t } = useI18n();

    const [voiceConfig, setVoiceConfig] = useState<GoogleVoiceConfig>(() => parseInitialConfig(formData.voice_config));

    // Derived lists
    const languages = useMemo(() => extractUniqueLanguages(availableVoices), [availableVoices]);
    const accents = useMemo(() =>
        voiceConfig.language ? extractAccentsForLanguage(availableVoices, voiceConfig.language) : [],
        [availableVoices, voiceConfig.language]
    );
    const voiceOptions = useMemo(() =>
        voiceConfig.language && voiceConfig.accent
            ? extractVoicesForAccent(availableVoices, voiceConfig.language, voiceConfig.accent)
            : [],
        [availableVoices, voiceConfig.language, voiceConfig.accent]
    );

    const isVoiceSelected = voiceConfig.language && voiceConfig.accent && voiceConfig.voice;

    // Audio state
    const [testText, setTestText] = useState('');
    const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [audioError, setAudioError] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    const handlePlayAudio = useCallback(() => {
        if (audioRef.current && audioUrl) audioRef.current.play();
    }, [audioUrl]);

    const handleTestVoice = useCallback(async () => {
        if (!testText.trim()) { setAudioError('Ingresa un texto para probar la voz'); return; }
        if (!voiceConfig.voiceApiName) { setAudioError('Re-selecciona la voz para actualizar la configuración'); return; }

        if (audioUrl) { URL.revokeObjectURL(audioUrl); setAudioUrl(null); }
        setIsGeneratingAudio(true);
        setAudioError(null);

        try {
            const tenantSlug = localStorage.getItem('tenant-slug') || 'main';
            const baseUrl = api.defaults.baseURL || '';
            const response = await fetch(`${baseUrl}/api/ai/profiles/test-tts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': tenantSlug },
                credentials: 'include',
                body: JSON.stringify({ provider_id: formData.provider_id, text: testText.trim(), voice_config: voiceConfig }),
            });

            if (response.ok) {
                const blob = await response.blob();
                setAudioUrl(URL.createObjectURL(blob));
            } else {
                const err = await response.json().catch(() => ({ message: 'Error generating audio' }));
                setAudioError(err.message || 'Error al generar audio');
            }
        } catch (error: unknown) {
            setAudioError((error as Error)?.message || 'Error de conexión');
        } finally {
            setIsGeneratingAudio(false);
        }
    }, [testText, voiceConfig, formData.provider_id, audioUrl]);

    // Config update + propagation
    const updateConfig = useCallback((updates: Partial<GoogleVoiceConfig>) => {
        const newConfig = { ...voiceConfig, ...updates };
        setVoiceConfig(newConfig);
        onInputChange('voice_config', JSON.stringify(newConfig));
        if (updates.voiceFullName) onInputChange('model', updates.voiceFullName);
    }, [voiceConfig, onInputChange]);

    // Cascade handlers
    const handleLanguageChange = (lang: string) => updateConfig({ language: lang, accent: '', voice: '', voiceFullName: '', gender: '' });
    const handleAccentChange = (acc: string) => updateConfig({ accent: acc, voice: '', voiceFullName: '', gender: '' });
    const handleVoiceChange = (voiceType: string) => {
        const sel = voiceOptions.find(v => v.voiceType === voiceType);
        if (sel) updateConfig({ voice: voiceType, voiceFullName: sel.fullName, voiceApiName: sel.apiName, gender: sel.gender });
    };

    const languageOptions = useMemo(() =>
        languages.map(lang => ({ value: lang, label: lang, checked: voiceConfig.language === lang })),
        [languages, voiceConfig.language]
    );

    return (
        <FormBox title={t('aiProfiles.sections.voiceConfiguration') || 'Voice Configuration'} icon={<Volume2 />}>
            <div className="space-y-6">
                {/* Cascading selectors: Language → Accent → Voice → Style */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <Globe className="h-4 w-4" />
                            {t('aiProfiles.googleTts.language') || 'Idioma'}
                        </Label>
                        <Combobox
                            single
                            options={languageOptions}
                            onToggle={handleLanguageChange}
                            placeholder={t('aiProfiles.googleTts.selectLanguage') || 'Seleccionar idioma'}
                            translations={{ searchOptions: t('common.search') || 'Buscar...', noOptionsFound: t('common.noResults') || 'No encontrado' }}
                        />
                    </div>

                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <MapPin className="h-4 w-4" />
                            {t('aiProfiles.googleTts.accent') || 'Acento'}
                        </Label>
                        <Select
                            value={voiceConfig.accent}
                            onValueChange={handleAccentChange}
                            options={accents.map(acc => ({ value: acc, label: acc }))}
                            placeholder={t('aiProfiles.googleTts.selectAccent') || 'Seleccionar acento'}
                            disabled={!voiceConfig.language || accents.length === 0}
                        />
                    </div>

                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <Mic className="h-4 w-4" />
                            {t('aiProfiles.googleTts.voiceStyle') || 'Voz'}
                        </Label>
                        {voiceConfig.accent && voiceOptions.length > 0 ? (
                            <Combobox
                                single
                                options={voiceOptions.map(v => ({
                                    value: v.voiceType,
                                    label: `${v.voiceType}${v.gender ? ` (${v.gender})` : ''}`,
                                    checked: voiceConfig.voice === v.voiceType,
                                }))}
                                onToggle={handleVoiceChange}
                                placeholder={t('aiProfiles.googleTts.selectVoice') || 'Seleccionar voz'}
                                translations={{ searchOptions: t('common.search') || 'Buscar...', noOptionsFound: t('common.noResults') || 'No encontrado' }}
                            />
                        ) : (
                            <Select value="" onValueChange={() => { }} options={[]}
                                placeholder={t('aiProfiles.googleTts.selectVoice') || 'Seleccionar voz'} disabled />
                        )}
                    </div>

                    <div>
                        <Label className="flex items-center gap-2 mb-2">
                            <Music className="h-4 w-4" />
                            {t('aiProfiles.googleTts.styleLabel') || 'Estilo'}
                        </Label>
                        <Select
                            value={voiceConfig.style}
                            onValueChange={(style) => updateConfig({ style })}
                            options={VOICE_STYLES}
                            placeholder={t('aiProfiles.googleTts.selectStyle') || 'Seleccionar estilo'}
                            disabled={!voiceConfig.voice}
                        />
                    </div>
                </div>

                {/* Audio controls + test panel — only shown after full voice selection */}
                {isVoiceSelected && (
                    <>
                        <GoogleVoiceAudioControls
                            speakingRate={voiceConfig.speakingRate}
                            pitch={voiceConfig.pitch}
                            volumeGainDb={voiceConfig.volumeGainDb}
                            onUpdate={updateConfig}
                        />
                        <GoogleVoiceTestPanel
                            audioRef={audioRef}
                            audioUrl={audioUrl}
                            audioError={audioError}
                            isGeneratingAudio={isGeneratingAudio}
                            testText={testText}
                            onTestTextChange={setTestText}
                            onGenerate={handleTestVoice}
                            onPlay={handlePlayAudio}
                        />
                    </>
                )}

                {!isVoiceSelected && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                        {t('aiProfiles.googleTts.selectVoiceHint') || 'Selecciona idioma, acento y voz para ver las opciones de audio'}
                    </p>
                )}
            </div>
        </FormBox>
    );
}
